import { useEffect, useRef, useState, useCallback } from 'react';
import s from './CameraCapture.module.css';

export function CameraCapture({ appt, onClose, onUpload }) {
  const videoRef  = useRef(null);
  const streamRef = useRef(null);
  const trackRef  = useRef(null);
  const shotsRef  = useRef([]);
  const focusTimer = useRef(null);
  const [shots, setShots]         = useState([]);
  const [error, setError]         = useState('');
  const [uploadError, setUploadError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress]   = useState(null); // { done, total } while uploading
  const [focus, setFocus]         = useState(null); // { x, y, id } in preview px

  shotsRef.current = shots;

  useEffect(() => {
    let cancelled = false;
    async function start() {
      if (!window.isSecureContext) {
        setError('Camera needs a secure connection. Open this page over HTTPS (or via localhost) — it will not work over plain http://.');
        return;
      }
      if (!navigator.mediaDevices?.getUserMedia) {
        setError('Camera is not supported on this device/browser.');
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
            width:  { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }
        streamRef.current = stream;

        // Request autofocus where the device/browser supports it (Android
        // Chrome). Prefer continuous; fall back to single-shot. iOS Safari
        // ignores this but autofocuses natively.
        const [track] = stream.getVideoTracks();
        trackRef.current = track;
        const modes = track?.getCapabilities?.().focusMode || [];
        const focusMode = modes.includes('continuous') ? 'continuous'
          : modes.includes('single-shot') ? 'single-shot'
          : null;
        if (focusMode) {
          try { await track.applyConstraints({ advanced: [{ focusMode }] }); } catch {}
        }

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
      } catch {
        setError('Unable to access the camera. Allow camera permission, and note the page must be served over HTTPS (or localhost).');
      }
    }
    start();
    return () => {
      cancelled = true;
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, []);

  useEffect(() => () => shotsRef.current.forEach(sh => URL.revokeObjectURL(sh.url)), []);
  useEffect(() => () => { if (focusTimer.current) clearTimeout(focusTimer.current); }, []);

  // Tap-to-focus. Drives the lens to the tapped point via the pointsOfInterest /
  // single-shot constraints where the device supports it (mostly Android Chrome).
  // The focus ring is shown ONLY when a real refocus was actually applied, so it
  // never gives fake feedback. iOS Safari / unsupported cameras keep autofocusing
  // natively and simply show no ring.
  const tapToFocus = useCallback(async (e) => {
    const video = videoRef.current;
    const track = trackRef.current;
    if (!video || !track?.getCapabilities) return;

    const rect = video.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    const nx = Math.min(1, Math.max(0, px / rect.width));
    const ny = Math.min(1, Math.max(0, py / rect.height));

    const caps = track.getCapabilities();
    const constraint = {};
    if ((caps.focusMode || []).includes('single-shot')) constraint.focusMode = 'single-shot';
    if (caps.pointsOfInterest) constraint.pointsOfInterest = [{ x: nx, y: ny }];
    if (!Object.keys(constraint).length) return; // device can't focus-on-tap → no ring

    try {
      await track.applyConstraints({ advanced: [constraint] });
    } catch {
      return; // refocus rejected → don't show a misleading ring
    }

    setFocus({ x: px, y: py, id: Date.now() });
    if (focusTimer.current) clearTimeout(focusTimer.current);
    focusTimer.current = setTimeout(() => setFocus(null), 900);
  }, []);

  const capture = useCallback(() => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width  = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
      const url  = URL.createObjectURL(blob);
      setShots(prev => [...prev, { url, file }]);
    }, 'image/jpeg', 0.92);
  }, []);

  const remove = useCallback((index) => {
    setShots(prev => {
      const next = [...prev];
      const [removed] = next.splice(index, 1);
      if (removed) URL.revokeObjectURL(removed.url);
      return next;
    });
  }, []);

  const handleUpload = useCallback(async () => {
    if (!shots.length) return;
    setUploading(true);
    setUploadError('');

    const total = shots.length;
    const failed = [];
    let done = 0;
    setProgress({ done, total });

    for (const shot of shots) {
      try {
        await onUpload([shot.file], appt);
        URL.revokeObjectURL(shot.url);
        done += 1;
        setProgress({ done, total });
      } catch {
        failed.push(shot);
      }
    }

    if (failed.length) {
      setShots(failed);
      setUploadError(`${failed.length} of ${total} photo(s) failed. Tap Upload to retry.`);
      setUploading(false);
      setProgress(null);
    } else {
      onClose();
    }
  }, [shots, onUpload, appt, onClose]);

  return (
    <div className={s.overlay}>
      <div className={s.topbar}>
        <button className={s.closeBtn} onClick={onClose} aria-label="Close camera">✕</button>
        <span className={s.title}>{`${appt?.car} Appointment Photos` || 'Photos'}</span>
        <span className={s.count}>{shots.length}</span>
      </div>

      <div className={s.videoWrap}>
        {error
          ? <div className={s.error}>{error}</div>
          : <>
              <video ref={videoRef} className={s.video} playsInline muted autoPlay onClick={tapToFocus} />
              {focus && <span key={focus.id} className={s.focusRing} style={{ left: focus.x, top: focus.y }} />}
            </>}
      </div>

      <div className={s.strip}>
        {uploadError && <div className={s.stripHint} style={{ color: 'var(--urgent-text)' }}>{uploadError}</div>}
        {shots.length === 0 && !uploadError && <div className={s.stripHint}>No photos yet — tap the shutter</div>}
        {shots.map((sh, i) => (
          <div key={sh.url} className={s.thumbWrap}>
            <img src={sh.url} alt={`Photo ${i + 1}`} className={s.thumb} />
            <button className={s.thumbX} onClick={() => remove(i)} aria-label={`Remove photo ${i + 1}`}>✕</button>
          </div>
        ))}
      </div>

      <div className={s.controls}>
        <div className={s.controlSide} />
        <button className={s.shutter} onClick={capture} disabled={!!error} aria-label="Take photo">
          <span className={s.shutterInner} />
        </button>
        <div className={`${s.controlSide} ${s.controlSideRight}`}>
          <button className={s.uploadBtn} onClick={handleUpload} disabled={!shots.length || uploading}>
            {uploading
              ? (progress ? `Uploading ${progress.done}/${progress.total}…` : 'Uploading…')
              : `Upload${shots.length ? ` ${shots.length}` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}
